import { useState, useEffect, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Empresa } from '@/lib/types'
import GoogleWorkspaceCard from '@/components/settings/GoogleWorkspaceCard'
import ChannelVisibilityConfig from '@/components/settings/ChannelVisibilityConfig'
import BuzonRemitentes from '@/components/settings/BuzonRemitentes'
import RecepcionistaConfig from '@/components/settings/RecepcionistaConfig'
import BotDelFrontdesk from '@/components/settings/BotDelFrontdesk'
import Agentes from '@/pages/Agentes'
import { PageHeader } from '@/components/ui'

type SettingsTab =
  | 'general' | 'firma_email'
  | 'canales' | 'chatbot' | 'recepcionista' | 'soporte' | 'pedidos' | 'estado'
  | 'buzon'

/**
 * El menú dejó de ser una lista plana: con el chatbot y el recepcionista adentro, seis
 * ítems sueltos no dicen qué tiene que ver cada uno con el otro. «Comunicaciones» junta
 * todo lo que le habla a un cliente.
 *
 * Las claves no cambiaron (`canales`, `general`…), así que los enlaces viejos del tipo
 * `?tab=canales` siguen funcionando.
 */
const SETTINGS_GRUPOS: { titulo: string; items: { key: SettingsTab; label: string }[] }[] = [
  {
    titulo: 'General',
    items: [
      { key: 'general', label: 'Datos de la empresa' },
      { key: 'firma_email', label: 'Firma de Email' },
    ],
  },
  {
    titulo: 'Comunicaciones',
    items: [
      { key: 'canales', label: 'Canales' },
      { key: 'chatbot', label: 'Chatbot web' },
      { key: 'recepcionista', label: 'Recepcionista de WhatsApp' },
      { key: 'soporte', label: 'Soporte de WhatsApp' },
      { key: 'pedidos', label: 'Pedidos de WhatsApp' },
      { key: 'estado', label: 'Estado de pedidos' },
    ],
  },
  {
    titulo: 'Buzón',
    items: [
      { key: 'buzon', label: 'Remitentes' },
    ],
  },
]

export default function Configuracion() {
  const db = useSupabase()
  const [searchParams, setSearchParams] = useSearchParams()
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const activeTab: SettingsTab = (searchParams.get('tab') as SettingsTab) || 'general'

  useEffect(() => {
    const googleStatus = searchParams.get('google')
    if (googleStatus === 'connected') {
      showToast('Google Workspace conectado exitosamente', 'success')
      setSearchParams({}, { replace: true })
    } else if (googleStatus === 'error') {
      const reason = searchParams.get('reason') ?? 'desconocido'
      showToast(`Error al conectar Google: ${reason}`, 'error')
      setSearchParams({}, { replace: true })
    }
  }, [searchParams])

  const [form, setForm] = useState<Omit<Empresa, 'id'>>({
    nombre: '', razon_social: '', ruc: '', telefono: '', email: '', website: '', ubicacion: '', logo_url: '',
    color_primario: '#1C4587', color_secundario: '#FFFFFF',
    banco: '', cuenta_corriente: '', cci: '', swift: '', direccion_banco: '',
    cuenta_corriente_pen: '', cci_pen: '',
    google_tokens: null, google_connected: false, canales_config: null, firma_email_html: null,
  })

  useEffect(() => {
    (async () => {
      try {
        const data = await db.getAll<Empresa>('empresa')
        if (data.length > 0) {
          setEmpresa(data[0])
          const { id: _, ...rest } = data[0]
          setForm(rest as Omit<Empresa, 'id'>)
        }
      } catch { showToast('Error al cargar configuración', 'error') }
      finally { setLoading(false) }
    })()
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (empresa) {
        const clean = { ...form };
        await db.update('empresa', { id: empresa.id, ...clean } as Record<string, unknown>)
        showToast('Configuración guardada', 'success')
      }
    } catch { showToast('Error al guardar', 'error') }
    finally { setSaving(false) }
  }

  const set = (key: keyof Omit<Empresa, 'id'>, val: string) => setForm(f => ({ ...f, [key]: val }))

  const handleCanalConfigChange = (config: Empresa['canales_config']) => {
    setForm(f => ({ ...f, canales_config: config }))
  }

  // El nombre de la pestana activa sale del mismo catalogo que dibuja el menu,
  // asi que el crumb no se puede desincronizar de lo que el usuario pulso.
  const tituloTab = SETTINGS_GRUPOS
    .flatMap(g => g.items)
    .find(it => it.key === activeTab)?.label ?? 'General'

  const setTab = (tab: SettingsTab) => {
    setSearchParams({ tab }, { replace: true })
  }

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Configuración' }]} /><div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Configuración' }, { label: tituloTab }]}
        actions={(activeTab === 'general' || activeTab === 'firma_email') && (
          <button className="btn btn-primary" onClick={submit} disabled={saving} id="btn-save-config" type="button">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        )}
      />

      <div className="settings-layout">
        {/* Settings Sidebar */}
        <nav className="settings-sidebar">
          {SETTINGS_GRUPOS.map(grupo => (
            <div className="settings-sidebar__grupo" key={grupo.titulo}>
              <span className="settings-sidebar__titulo">{grupo.titulo}</span>
              {grupo.items.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  className={`settings-sidebar__item ${activeTab === tab.key ? 'settings-sidebar__item--active' : ''}`}
                  onClick={() => setTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Settings Content */}
        <div className="settings-content">
          {activeTab === 'general' && (
            <form onSubmit={submit}>
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header"><h2>Datos Generales</h2></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Nombre Comercial</label><input className="form-input" value={form.nombre ?? ''} onChange={e => set('nombre', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Razón Social</label><input className="form-input" value={form.razon_social ?? ''} onChange={e => set('razon_social', e.target.value)} /></div>
                </div>
                <div className="form-row-3">
                  <div className="form-group"><label className="form-label">RUC</label><input className="form-input" value={form.ruc ?? ''} onChange={e => set('ruc', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono ?? ''} onChange={e => set('telefono', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website ?? ''} onChange={e => set('website', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Ubicación</label><input className="form-input" value={form.ubicacion ?? ''} onChange={e => set('ubicacion', e.target.value)} /></div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header"><h2>Branding</h2></div>
                <div className="form-row-3">
                  <div className="form-group"><label className="form-label">Logo (URL)</label><input className="form-input" value={form.logo_url ?? ''} onChange={e => set('logo_url', e.target.value)} placeholder="logo.webp" /></div>
                  <div className="form-group">
                    <label className="form-label">Color Primario</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={form.color_primario ?? '#1C4587'} onChange={e => set('color_primario', e.target.value)} style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                      <input className="form-input" value={form.color_primario ?? ''} onChange={e => set('color_primario', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Color Secundario</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={form.color_secundario ?? '#FFFFFF'} onChange={e => set('color_secundario', e.target.value)} style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                      <input className="form-input" value={form.color_secundario ?? ''} onChange={e => set('color_secundario', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16, padding: 20, borderRadius: 'var(--radius)', background: form.color_primario ?? 'var(--primary)', color: form.color_secundario ?? '#FFF', textAlign: 'center' }}>
                  <strong style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem' }}>{form.nombre || '1Conecta'}</strong>
                  <p style={{ opacity: 0.8, fontSize: '0.85rem', marginTop: 4 }}>{form.razon_social || 'Vista previa del branding'}</p>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header"><h2>Datos Bancarios</h2></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Banco</label><input className="form-input" value={form.banco ?? ''} onChange={e => set('banco', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Cuenta Corriente</label><input className="form-input" value={form.cuenta_corriente ?? ''} onChange={e => set('cuenta_corriente', e.target.value)} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">CCI</label><input className="form-input" value={form.cci ?? ''} onChange={e => set('cci', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">SWIFT</label><input className="form-input" value={form.swift ?? ''} onChange={e => set('swift', e.target.value)} /></div>
                </div>
                {/* Las cotizaciones y facturas en soles imprimen esta cuenta, no la de
                    dólares: el banco convierte el abono al tipo de cambio del día. */}
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Cuenta Corriente Soles</label><input className="form-input" value={form.cuenta_corriente_pen ?? ''} onChange={e => set('cuenta_corriente_pen', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">CCI Soles</label><input className="form-input" value={form.cci_pen ?? ''} onChange={e => set('cci_pen', e.target.value)} /></div>
                </div>
                <div className="form-group"><label className="form-label">Dirección del Banco</label><input className="form-input" value={form.direccion_banco ?? ''} onChange={e => set('direccion_banco', e.target.value)} /></div>
              </div>
            </form>
          )}

          {activeTab === 'canales' && (
            <>
              <ChannelVisibilityConfig
                empresa={empresa}
                empresaId={empresa?.id}
                onConfigChange={handleCanalConfigChange}
              />
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header"><h2>Integraciones</h2></div>
                <GoogleWorkspaceCard />
              </div>
            </>
          )}

          {activeTab === 'buzon' && <BuzonRemitentes />}

          {/* El mismo módulo de `/agentes`, sin su encabezado: acá ya hay uno. */}
          {activeTab === 'chatbot' && <Agentes embebido />}

          {activeTab === 'recepcionista' && <RecepcionistaConfig />}

          {activeTab === 'soporte' && (
            <BotDelFrontdesk
              slug="soporte-whatsapp"
              titulo="Soporte de WhatsApp"
              claveResumen="error"
              datos={[
                { clave: 'error', titulo: 'Qué dice' },
                { clave: 'correo_afectado', titulo: 'Quién' },
                { clave: 'donde', titulo: 'Dónde' },
                { clave: 'captura', titulo: 'Captura' },
              ]}
              vacio="Todavía no recogió ninguno. Aparecerá acá cuando un cliente reporte una falla y nadie del equipo le contesta en 5 minutos."
              descripcionFlujo={<>
                Un paso por dato, y los que el cliente ya contó se saltan solos.
                <strong> Guardar</strong> deja un borrador que sólo vos ves; el bot sigue con
                lo anterior hasta que pulses <strong>Publicar</strong>.
              </>}
              quePasa={<>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  Es el <strong>espejo del recepcionista</strong>: habla sólo con clientes que
                  ya tenemos, y nunca con desconocidos ni en grupos. Cuando alguien reporta una
                  falla, le pide lo que falta y deja el reporte como tarea.
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  <strong>Espera 5 minutos antes de abrir la boca</strong>, y puede darse otra
                  vuelta si vos seguís contestando WhatsApp en ese momento o si el cliente
                  sigue escribiendo.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: 0 }}>
                  Qué pregunta salió de replicar tus reportes reales: el error primero, el
                  correo del afectado <strong>sólo si la falla es de otra persona</strong>, y
                  el «dónde» sólo si no vino captura.
                </p>
              </>}
            />
          )}

          {activeTab === 'pedidos' && (
            <BotDelFrontdesk
              slug="pedidos-whatsapp"
              titulo="Pedidos de WhatsApp"
              claveResumen="pedido"
              vacio="Todavía no anotó ninguno. Aparecerá acá cuando un cliente pida algo y nadie del equipo le contesta en 5 minutos."
              descripcionFlujo={<>
                Un solo mensaje: acusa recibo y nombra la tarea. <strong>Guardar</strong> deja
                un borrador que sólo vos ves; el bot sigue con lo anterior hasta que pulses
                <strong> Publicar</strong>.
              </>}
              quePasa={<>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  <strong>Este bot no pregunta nada.</strong> Acusa recibo de lo que un cliente
                  pide y lo deja anotado como tarea, para que no espere horas sin una palabra
                  ni se pierda si nadie lo pasa al Kanban.
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  Que no pregunte no es una limitación: de tus 15 pedidos que tardaron más de
                  5 minutos, <strong>13 recibieron puro acuse de recibo</strong> («ok, lo
                  reviso»). Un bot que interrogara haría algo que vos no hacés.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: 0 }}>
                  Lo difícil acá es <strong>cuándo callarse</strong>: la mayoría de los
                  mensajes que parecen pedidos son turnos de una conversación («un ejemplo»,
                  «hazlo nomás», «y también fin de mes»). Sólo habla si el pedido se entiende
                  leyéndolo solo. Y si el cliente manda una contraseña, no la copia a la tarea.
                </p>
              </>}
            />
          )}

          {activeTab === 'estado' && (
            <BotDelFrontdesk
              slug="estado-whatsapp"
              titulo="Estado de pedidos (WhatsApp)"
              claveResumen="cuando"
              vacio="Todavía no contestó ninguna. Aparecerá acá cuando un cliente pregunte «¿cuándo estará?» por algo que tenga fecha puesta en su tarea."
              descripcionFlujo={<>
                Un solo mensaje: repite la fecha que ya está en la tarea. <strong>Guardar</strong> deja
                un borrador que sólo tú ves; el bot sigue con lo anterior hasta que pulses
                <strong> Publicar</strong>.
              </>}
              quePasa={<>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  Contesta <strong>«¿cuándo estará?»</strong> con la fecha que una persona ya
                  puso en la tarea. Es el único de los tres bots que le <strong>promete algo</strong> a
                  un cliente, así que es el que conviene poder apagar solo.
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 12px' }}>
                  La regla es una: <strong>sólo repite una fecha que pusiste tú</strong>. Ningún
                  bot escribe <code>fecha_limite</code>, así que si está, la puso alguien del
                  equipo desde el Kanban. No calcula plazos ni estima.
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: 0 }}>
                  Se calla si no hay nada con fecha en ese chat, si la fecha ya venció (un
                  atraso lo explica una persona) o si hay más de una tarea con fecha: ahí
                  habría que nombrarlas, y el título de una tarea está escrito para adentro.
                </p>
              </>}
            />
          )}

          {activeTab === 'firma_email' && (
            <>
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header">
                  <h2>Editor de Firma</h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>HTML de la firma que se incluirá en los correos transaccionales</p>
                </div>
                <div style={{ padding: '0 20px 20px' }}>
                  <textarea
                    className="form-input"
                    value={form.firma_email_html ?? ''}
                    onChange={e => setForm(f => ({ ...f, firma_email_html: e.target.value }))}
                    rows={20}
                    style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.5, resize: 'vertical' }}
                    placeholder="Pega aquí el HTML de tu firma de email..."
                  />
                </div>
              </div>

              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header">
                  <h2>Vista Previa</h2>
                </div>
                <div style={{ padding: '20px', background: '#fff', borderRadius: 'var(--radius)' }}>
                  {form.firma_email_html ? (
                    <div dangerouslySetInnerHTML={{ __html: form.firma_email_html }} />
                  ) : (
                    <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>No hay firma configurada</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
